import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, StatCard } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import {
  Users, GraduationCap, ClipboardCheck, Wallet, Boxes,
  UserSquare2, MessageSquareWarning, TrendingUp,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";

const COLORS = ["#1B2C5A", "#9DCD3F", "#2A3F7A", "#475569", "#14224A"];

const NGN = (v) => "₦" + (v || 0).toLocaleString();

const Overview = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [finance, setFinance] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => setError("Your dashboard could not be loaded. Please retry."));
    const roles = new Set(user?.roles || (user?.role ? [user.role] : []));
    if (["super_admin", "director", "accountant", "receptionist"].some((role) => roles.has(role))) {
      api.get("/finance/context").then(({ data: context }) => api.get("/finance/overview", { params: { session_id: context.session.id, term_id: context.term.id } })).then(({ data }) => setFinance(data)).catch(() => {});
    }
  }, [user]);

  if (error) return <Card className="p-6"><h1 className="font-display text-xl text-[#1B2C5A]">Dashboard unavailable</h1><p className="mt-2 text-sm text-slate-600">{error}</p><button className="mt-4 rounded bg-[#1B2C5A] px-4 py-2 text-white" onClick={() => window.location.reload()}>Retry</button></Card>;
  if (!stats) {
    return (
      <div className="text-[#475569] font-display" data-testid="overview-loading">Loading dashboard...</div>
    );
  }

  if (stats.dashboard_type === "self_service") return (
    <div data-testid="self-service-dashboard">
      <PageHeader overline={`Welcome, ${user?.name || "DFA staff"}`} title="My Dashboard" description="Your personal DFA account and authorized self-service tools." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Card className="p-5"><h2 className="font-semibold text-[#1B2C5A]">My Profile</h2><p className="mt-2 text-sm text-slate-600">Review your account and security settings.</p></Card>
        {stats.available_actions?.staff_attendance && <Card className="p-5"><h2 className="font-semibold text-[#1B2C5A]">My Attendance</h2><p className="mt-2 text-sm text-slate-600">Clock attendance and review your attendance information.</p></Card>}
        {stats.available_actions?.academic_assignments && <Card className="p-5"><h2 className="font-semibold text-[#1B2C5A]">My Academic Assignments</h2><p className="mt-2 text-sm text-slate-600">Open only the classes and subjects assigned or delegated to you.</p></Card>}
      </div>
    </div>
  );

  const genderData = Object.entries(stats.gender || {}).map(([k, v]) => ({ name: k, value: v }));
  const gradeData = stats.grades || [];

  return (
    <div data-testid="overview-page">
      <PageHeader
        overline={`Welcome, ${user?.name}`}
        title="Operations Overview"
        description="Live snapshot of admissions, attendance, fees and facility health across the academy."
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Active Students" value={stats.students_total} icon={GraduationCap} testId="stat-students" />
        <StatCard label="Active Staff" value={stats.staff_total} icon={Users} testId="stat-staff" />
        <StatCard label="Present Today" value={stats.present_today} icon={ClipboardCheck} accent testId="stat-present" />
        <StatCard label="Absent Today" value={stats.absent_today} icon={ClipboardCheck} testId="stat-absent" />
        {finance && <StatCard label="Fees Collected" value={NGN(finance.total_collected_minor / 100)} icon={Wallet} testId="stat-fees-paid" />}
        {finance && <StatCard label="Fees Pending" value={NGN(finance.total_outstanding_minor / 100)} icon={Wallet} accent testId="stat-fees-pending" />}
        <StatCard label="Visitors Today" value={stats.visitors_today} icon={UserSquare2} testId="stat-visitors" />
        <StatCard label="Open Complaints" value={stats.open_complaints} icon={MessageSquareWarning} testId="stat-complaints" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 lg:col-span-2 border-[#E2E8F0]">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="overline text-[#9DCD3F]">7-Day Trend</div>
              <h3 className="font-display text-xl text-[#1B2C5A]">Student Attendance</h3>
            </div>
            <TrendingUp className="text-[#9DCD3F] w-5 h-5" />
          </div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={stats.attendance_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={11} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0" }} />
                <Legend />
                <Line type="monotone" dataKey="present" stroke="#1B2C5A" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="absent" stroke="#9DCD3F" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="late" stroke="#475569" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8F0]">
          <div className="overline text-[#9DCD3F]">Demographics</div>
          <h3 className="font-display text-xl text-[#1B2C5A] mb-3">Gender Mix</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={genderData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={90} label>
                  {genderData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6 lg:col-span-2 border-[#E2E8F0]">
          <div className="overline text-[#9DCD3F]">By Grade</div>
          <h3 className="font-display text-xl text-[#1B2C5A] mb-3">Enrolment Distribution</h3>
          <div className="h-72">
            <ResponsiveContainer>
              <BarChart data={gradeData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="grade" stroke="#475569" fontSize={11} />
                <YAxis stroke="#475569" fontSize={11} />
                <Tooltip />
                <Bar dataKey="count" fill="#1B2C5A" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card className="p-6 border-[#E2E8F0]">
          <div className="overline text-[#9DCD3F]">Inventory</div>
          <h3 className="font-display text-xl text-[#1B2C5A]">Low-Stock Alerts</h3>
          <div className="mt-6 flex items-center gap-3">
            <Boxes className="w-10 h-10 text-[#9DCD3F]" />
            <div>
              <div className="font-display text-4xl text-[#1B2C5A]">{stats.low_stock}</div>
              <div className="text-sm text-[#475569]">items at or below reorder level</div>
            </div>
          </div>
          <p className="mt-6 text-sm text-[#475569] leading-relaxed">
            The inventory officer should review and place reorders to avoid stockouts.
          </p>
        </Card>
      </div>
    </div>
  );
};

export default Overview;
